import type {
    OutreachDueReason,
    OutreachQueueSection,
    OutreachQueueTag,
} from "@athena/common/graphql/generated/schema";
import type { OutreachQueueQuery } from "@/graphql/clients.generated";
import {
    CHANNEL_FOR_CONTACT_TYPE,
    parseCalendarDate,
    toCalendarDate,
    toClientRecord,
    type ClientRecord,
    type ContactChannel,
} from "./client";

/**
 * The outreach queue as the page draws it. The API decides who is in it, when each is due, and
 * which section they fall in (`Query.outreachQueue`); this only maps its answer into the app's
 * shapes.
 */

/** The queue's sections, most urgent first. Only scheduled follow-ups are ever `later`. */
export const QUEUE_SECTIONS = [
    "overdue",
    "today",
    "thisWeek",
    "later",
] as const;
export type QueueSection = (typeof QUEUE_SECTIONS)[number];

/** The API's name for each section. */
export const API_SECTION_FOR_SECTION = {
    overdue: "OVERDUE",
    today: "DUE_TODAY",
    thisWeek: "THIS_WEEK",
    later: "LATER",
} as const satisfies Record<QueueSection, OutreachQueueSection>;

/**
 * The queue's filter tags, in the order they are drawn. The API puts every entry under exactly one
 * (`OutreachQueueTag`), so their counts add up to the whole queue.
 */
export const QUEUE_TAGS = [
    "overdue",
    "today",
    "thisWeek",
    "milestones",
    "scheduled",
] as const;
export type QueueTag = (typeof QUEUE_TAGS)[number];

/** The API's name for each tag. */
export const API_TAG_FOR_TAG = {
    overdue: "OVERDUE",
    today: "DUE_TODAY",
    thisWeek: "THIS_WEEK",
    milestones: "MILESTONES",
    scheduled: "SCHEDULED",
} as const satisfies Record<QueueTag, OutreachQueueTag>;

/**
 * The day `Query.outreachQueue` is asked about: the coordinator's, so what is overdue and who was
 * contacted today are theirs and not the server's. Shared by the queue and the nav's count of it,
 * so the two cannot disagree about which day it is.
 */
export const queueDay = (today: Date): { today: string; todayStartsAt: string } => ({
    today: toCalendarDate(today),
    // Local midnight: "contacted today" is the coordinator's day, not UTC's.
    todayStartsAt: new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString(),
});

/** A milestone entry's badge: which kind, and for an anniversary which one. */
export type QueueMilestone = { kind: "birthday" } | { kind: "soberAnniversary"; years: number };

/** The reverse of `API_TAG_FOR_TAG`, derived so the two cannot disagree. */
const TAG_FOR_API_TAG = Object.fromEntries(
    Object.entries(API_TAG_FOR_TAG).map(
        ([
            tag,
            apiTag,
        ]) => [
            apiTag,
            tag,
        ],
    ),
) as Record<OutreachQueueTag, QueueTag>;

/** The reverse of `API_SECTION_FOR_SECTION`, derived so the two cannot disagree. */
const SECTION_FOR_API_SECTION = Object.fromEntries(
    Object.entries(API_SECTION_FOR_SECTION).map(
        ([
            section,
            apiSection,
        ]) => [
            apiSection,
            section,
        ],
    ),
) as Record<OutreachQueueSection, QueueSection>;

export interface QueueEntry {
    /** Stable from one fetch to the next. */
    id: string;
    client: ClientRecord;
    /** `YYYY-MM-DD`. */
    dueDate: string;
    /** Whole days past due: 0 today, negative while still ahead. */
    daysOverdue: number;
    section: QueueSection;
    /** The one filter tag it is under. */
    tag: QueueTag;
    /** Why a contact-due entry is due. Unset for a milestone, which `milestone` describes instead. */
    reason?: OutreachDueReason;
    /** Set for a birthday or sober anniversary. */
    milestone?: QueueMilestone;
    /**
     * How the next contact is meant to happen, when a follow-up scheduled from the profile planned
     * it. The quick log skips "How did you connect?" when this is set.
     */
    channel?: ContactChannel;
    /** The prep note on a follow-up scheduled from the profile. */
    prepNote?: string;
}

type ApiQueueEntry = OutreachQueueQuery["outreachQueue"]["entries"][number];

const yearsBetween = (from: string, to: string): number =>
    parseCalendarDate(to).getFullYear() - parseCalendarDate(from).getFullYear();

const milestoneFor = (entry: ApiQueueEntry): QueueMilestone | undefined => {
    if (entry.kind === "BIRTHDAY") return { kind: "birthday" };
    if (entry.kind === "SOBER_ANNIVERSARY" && entry.client.soberDate) {
        return { kind: "soberAnniversary", years: yearsBetween(entry.client.soberDate, entry.dueDate) };
    }
    return undefined;
};

export const toQueueEntry = (entry: ApiQueueEntry): QueueEntry => {
    // Only a contact-due entry is the follow-up; the same client's birthday is not.
    const followUp = entry.kind === "CONTACT_DUE" ? entry.client.scheduledFollowUp : null;
    return {
        id: entry.id,
        client: toClientRecord(entry.client),
        dueDate: entry.dueDate,
        daysOverdue: entry.daysOverdue,
        section: SECTION_FOR_API_SECTION[entry.section],
        tag: TAG_FOR_API_TAG[entry.tag],
        reason: entry.reason ?? undefined,
        milestone: milestoneFor(entry),
        channel: followUp ? CHANNEL_FOR_CONTACT_TYPE[followUp.type] : undefined,
        prepNote: followUp?.notes ?? undefined,
    };
};
